import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "feedback-container" }
const _hoisted_2 = { class: "card-title" }
const _hoisted_3 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "feedback-title" }
const _hoisted_6 = { class: "feedback-meta" }
const _hoisted_7 = { class: "feedback-content" }
const _hoisted_8 = {
  key: 0,
  class: "admin-reply"
}

import { ref, reactive, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import {
  MessageOutlined,
  SendOutlined,
  CalendarOutlined,
  TagOutlined
} from '@ant-design/icons-vue';
import { submitFeedback, getMyFeedbacks } from '@/servers/api/feedback';
import { useUserStore } from '@/store';

// 定义反馈项类型
interface FeedbackItem {
  id: number;
  user_id?: string;
  user_email?: string;
  user_name?: string;
  feedback_type: string;
  title: string;
  content: string;
  contact_method: string;
  contact_value: string;
  priority: string;
  status: string;
  admin_reply?: string;
  created_at: string;
  updated_at?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Feedback',
  setup(__props) {

const userStore = useUserStore();
const activeTab = ref('submit');
const submitting = ref(false);
const loading = ref(false);
const feedbackList = ref<FeedbackItem[]>([]);
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showTotal: (total: number) => `共 ${total} 条`,
  onChange: (page: number, pageSize: number) => {
    pagination.current = page;
    pagination.pageSize = pageSize;
    loadMyFeedbacks();
  }
});

const formData = reactive<API.FeedbackSubmit>({
  feedback_type: 'bug',
  title: '',
  content: '',
  contact_method: 'email',
  contact_value: '',
  priority: 'normal'
});

// 联系方式标签
const contactLabel = computed(() => {
  const labels = {
    email: '邮箱',
    phone: '手机号',
    wechat: '微信号'
  };
  return labels[formData.contact_method] || '联系方式';
});

// 验证联系方式
const validateContact = (_rule: any, value: string) => {
  // 如果没有输入联系方式，则不进行验证
  if (!value) {
    return Promise.resolve();
  }

  if (formData.contact_method === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return Promise.reject('请输入有效的邮箱地址');
    }
  } else if (formData.contact_method === 'phone') {
    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(value)) {
      return Promise.reject('请输入有效的手机号');
    }
  }

  return Promise.resolve();
};

// 提交反馈
const handleSubmit = async () => {
  submitting.value = true;
  try {
    const user = userStore.getUserInfo();
    const params: API.FeedbackSubmit = {
      ...formData,
      role: user.role,
      user_email: user.email,
      user_name: user.student?.name || user.teacher?.name || '匿名用户'
    };

    await submitFeedback(params);
    message.success('反馈提交成功，感谢您的宝贵意见！');
    handleReset();
    activeTab.value = 'history';
    loadMyFeedbacks();
  } catch (error) {
    message.error('提交失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
};

// 重置表单
const handleReset = () => {
  formData.feedback_type = 'bug';
  formData.title = '';
  formData.content = '';
  formData.contact_method = 'email';
  formData.contact_value = '';
  formData.priority = 'normal';
};

// 加载我的反馈
const loadMyFeedbacks = async () => {
  loading.value = true;
  try {
    const res = await getMyFeedbacks({
      page: pagination.current,
      page_size: pagination.pageSize
    });
    feedbackList.value = res.data || [];
    pagination.total = res.total || 0;
  } catch (error) {
    message.error('加载反馈列表失败');
  } finally {
    loading.value = false;
  }
};

// 获取状态颜色
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'orange',
    processing: 'blue',
    resolved: 'green',
    closed: 'default'
  };
  return colors[status] || 'default';
};

// 获取状态文本
const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    pending: '待处理',
    processing: '处理中',
    resolved: '已解决',
    closed: '已关闭'
  };
  return texts[status] || status;
};

// 获取类型颜色
const getTypeColor = (type: string) => {
  const colors: Record<string, string> = {
    bug: 'red',
    feature: 'blue',
    improvement: 'green',
    other: 'default'
  };
  return colors[type] || 'default';
};

// 获取类型文本
const getTypeText = (type: string) => {
  const texts: Record<string, string> = {
    bug: '问题反馈',
    feature: '功能建议',
    improvement: '优化建议',
    other: '其他'
  };
  return texts[type] || type;
};

// 获取优先级文本
const getPriorityText = (priority: string) => {
  const texts: Record<string, string> = {
    low: '低',
    normal: '普通',
    high: '高',
    urgent: '紧急'
  };
  return texts[priority] || priority;
};

onMounted(() => {
  loadMyFeedbacks();
});

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_textarea = _resolveComponent("a-textarea")!
  const _component_a_radio = _resolveComponent("a-radio")!
  const _component_a_radio_group = _resolveComponent("a-radio-group")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_tab_pane = _resolveComponent("a-tab-pane")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_list_item_meta = _resolveComponent("a-list-item-meta")!
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_list_item = _resolveComponent("a-list-item")!
  const _component_a_list = _resolveComponent("a-list")!
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_a_tabs = _resolveComponent("a-tabs")!
  const _component_a_card = _resolveComponent("a-card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_a_card, {
      class: "feedback-card",
      bordered: false
    }, {
      title: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(MessageOutlined), { class: "title-icon" }),
          _cache[7] || (_cache[7] = _createElementVNode("span", null, "意见反馈", -1))
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_tabs, {
          activeKey: activeTab.value,
          "onUpdate:activeKey": _cache[6] || (_cache[6] = ($event: any) => ((activeTab).value = $event)),
          animated: true
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_tab_pane, {
              key: "submit",
              tab: "提交反馈"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_form, {
                  model: formData,
                  "label-col": { span: 4 },
                  "wrapper-col": { span: 16 },
                  onFinish: handleSubmit
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_form_item, {
                      label: "反馈类型",
                      name: "feedback_type",
                      rules: [{ required: true, message: '请选择反馈类型' }]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_select, {
                          value: formData.feedback_type,
                          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((formData.feedback_type) = $event)),
                          placeholder: "请选择反馈类型",
                          size: "large"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_select_option, { value: "bug" }, {
                              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                _createTextVNode("问题反馈", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "feature" }, {
                              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                _createTextVNode("功能建议", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "improvement" }, {
                              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                _createTextVNode("优化建议", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "other" }, {
                              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                _createTextVNode("其他", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["value"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_form_item, {
                      label: "反馈标题",
                      name: "title",
                      rules: [
                { required: true, message: '请输入反馈标题' },
                { min: 5, max: 100, message: '标题长度应在5-100个字符之间' }
              ]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_input, {
                          value: formData.title,
                          "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((formData.title) = $event)),
                          placeholder: "请简要描述您的问题或建议",
                          size: "large"
                        }, null, 8, ["value"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_form_item, {
                      label: "详细描述",
                      name: "content",
                      rules: [
                { required: true, message: '请输入详细描述' },
                { min: 10, message: '描述至少需要10个字符' }
              ]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_textarea, {
                          value: formData.content,
                          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((formData.content) = $event)),
                          placeholder: "请详细描述您遇到的问题或您的建议，以便我们更好地了解和处理",
                          rows: 6,
                          size: "large",
                          maxlength: 1000,
                          "show-count": ""
                        }, null, 8, ["value"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_form_item, {
                      label: "联系方式（可选）",
                      name: "contact_method"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_radio_group, {
                          value: formData.contact_method,
                          "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((formData.contact_method) = $event)),
                          size: "large"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_radio, { value: "email" }, {
                              default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                _createTextVNode("邮箱", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_radio, { value: "phone" }, {
                              default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                _createTextVNode("手机", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_radio, { value: "wechat" }, {
                              default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                _createTextVNode("微信", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["value"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_form_item, {
                      label: contactLabel.value,
                      name: "contact_value",
                      rules: [
                { validator: validateContact }
              ]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_input, {
                          value: formData.contact_value,
                          "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((formData.contact_value) = $event)),
                          placeholder: `请输入您的${contactLabel.value}`,
                          size: "large"
                        }, null, 8, ["value", "placeholder"])
                      ]),
                      _: 1
                    }, 8, ["label", "rules"]),
                    _createVNode(_component_a_form_item, {
                      label: "优先级",
                      name: "priority",
                      rules: [{ required: true, message: '请选择优先级' }]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_select, {
                          value: formData.priority,
                          "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((formData.priority) = $event)),
                          placeholder: "请选择优先级",
                          size: "large"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_select_option, { value: "low" }, {
                              default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                _createTextVNode("低", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "normal" }, {
                              default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                _createTextVNode("普通", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "high" }, {
                              default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                _createTextVNode("高", -1)
                              ]))]),
                              _: 1
                            }),
                            _createVNode(_component_a_select_option, { value: "urgent" }, {
                              default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                _createTextVNode("紧急", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["value"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_form_item, { "wrapper-col": { offset: 4, span: 16 } }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_space, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_a_button, {
                              type: "primary",
                              "html-type": "submit",
                              loading: submitting.value,
                              size: "large"
                            }, {
                              icon: _withCtx(() => [
                                _createVNode(_unref(SendOutlined))
                              ]),
                              default: _withCtx(() => [
                                _cache[19] || (_cache[19] = _createTextVNode(" 提交反馈 ", -1))
                              ]),
                              _: 1
                            }, 8, ["loading"]),
                            _createVNode(_component_a_button, {
                              onClick: handleReset,
                              size: "large"
                            }, {
                              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                _createTextVNode("重置", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["model"])
              ]),
              _: 1
            }),
            _createVNode(_component_a_tab_pane, {
              key: "history",
              tab: "当前反馈"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_spin, { spinning: loading.value }, {
                  default: _withCtx(() => [
                    (feedbackList.value.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createVNode(_component_a_empty, { description: "暂无反馈记录" })
                        ]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _createVNode(_component_a_list, {
                            "data-source": feedbackList.value,
                            pagination: pagination,
                            "item-layout": "vertical"
                          }, {
                            renderItem: _withCtx(({ item }) => [
                              _createVNode(_component_a_list_item, null, {
                                extra: _withCtx(() => [
                                  _createVNode(_component_a_tag, {
                                    color: getStatusColor(item.status)
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(getStatusText(item.status)), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["color"])
                                ]),
                                default: _withCtx(() => [
                                  _createVNode(_component_a_list_item_meta, null, {
                                    title: _withCtx(() => [
                                      _createVNode(_component_a_space, null, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_a_tag, {
                                            color: getTypeColor(item.feedback_type)
                                          }, {
                                            default: _withCtx(() => [
                                              _createTextVNode(_toDisplayString(getTypeText(item.feedback_type)), 1)
                                            ]),
                                            _: 2
                                          }, 1032, ["color"]),
                                          _createElementVNode("span", _hoisted_5, _toDisplayString(item.title), 1)
                                        ]),
                                        _: 2
                                      }, 1024)
                                    ]),
                                    description: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_6, [
                                        _createElementVNode("span", null, [
                                          _createVNode(_unref(CalendarOutlined)),
                                          _createTextVNode(" " + _toDisplayString(item.created_at), 1)
                                        ]),
                                        _createElementVNode("span", null, [
                                          _createVNode(_unref(TagOutlined)),
                                          _createTextVNode(" 优先级: " + _toDisplayString(getPriorityText(item.priority)), 1)
                                        ])
                                      ])
                                    ]),
                                    _: 2
                                  }, 1024),
                                  _createElementVNode("div", _hoisted_7, _toDisplayString(item.content), 1),
                                  (item.admin_reply)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createVNode(_component_a_alert, {
                                          type: "success",
                                          "show-icon": ""
                                        }, {
                                          message: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                            _createElementVNode("strong", null, "管理员回复：", -1)
                                          ]))]),
                                          description: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(item.admin_reply), 1)
                                          ]),
                                          _: 2
                                        }, 1024)
                                      ]))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 1
                          }, 8, ["data-source", "pagination"])
                        ]))
                  ]),
                  _: 1
                }, 8, ["spinning"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["activeKey"])
      ]),
      _: 1
    })
  ]))
}
}

})